import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from "react";
import myAxios from "../_services/axiositeachWrapper";
import Config from "../_config/config";

export interface EntityLabels {
  program?: string;
  school?: string;
  course?: string;
  term?: string;
  batch?: string;
  year?: string;
  /** Enrollment-form "Course" field label (resource courseId) — e.g. "Grade". */
  enrollmentCourse?: string;
  /** Enrollment category label (resource enrollmentCategory) — e.g. "Enrollment Status". */
  enrollmentCategory?: string;
}

interface EntityLabelsContextType {
  labels: EntityLabels;
  /** Singular display label for an entity, with default fallback. */
  label: (key: keyof EntityLabels, fallback: string) => string;
  /** Plural (appends 's' to the singular label). */
  labelPlural: (key: keyof EntityLabels, fallback: string) => string;
  loading: boolean;
}

const defaults: EntityLabels = {
  program: "Program",
  school: "School",
  course: "Course",
  term: "Term",
  batch: "Batch",
  year: "Year",
  enrollmentCourse: "Course",
  enrollmentCategory: "Enrollment Category",
};

const EntityLabelsContext = createContext<EntityLabelsContextType>({
  labels: defaults,
  label: (k, f) => defaults[k] ?? f,
  labelPlural: (k, f) => (defaults[k] ?? f) + "s",
  loading: true,
});

export const useEntityLabels = () => useContext(EntityLabelsContext);

interface Props {
  children: ReactNode;
}

export const EntityLabelsProvider: React.FC<Props> = ({ children }) => {
  // Start with empty state so call-site fallbacks fire until the API responds.
  const [labels, setLabels] = useState<EntityLabels>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await myAxios.get(Config.nodeSiteUrl + "v1/common/entity-labels");
        const data = (res?.data ?? {}) as { success?: boolean; labels?: EntityLabels };
        if (!cancelled && data.success && data.labels) {
          setLabels(data.labels);
        }
      } catch (err) {
        console.error("[EntityLabelsProvider] Failed to load entity labels:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<EntityLabelsContextType>(() => {
    const label = (key: keyof EntityLabels, fallback: string) => labels[key] || fallback;
    const labelPlural = (key: keyof EntityLabels, fallback: string) => {
      const s = labels[key] || fallback;
      return s.endsWith("s") ? s : s + "s";
    };
    return { labels, label, labelPlural, loading };
  }, [labels, loading]);

  return <EntityLabelsContext.Provider value={value}>{children}</EntityLabelsContext.Provider>;
};
